import React, { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { toast } from "sonner";
import {
  Clock, MapPin, ChevronRight, ChevronDown, CheckCircle2, Users, MessageCircle, Calendar, Loader2, CreditCard
} from "lucide-react";
import PageTransition from "../components/PageTransition";
import PublicHeader from "../components/PublicHeader";
import SeoFooter from "../components/SeoFooter";
import AddressAutocomplete from "../components/AddressAutocomplete";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CARD_FEE_PCT = 2.2;

export default function TourPackagePage() {
  const { slug } = useParams();
  const [tour, setTour] = useState(undefined);
  const [tourPackages, setTourPackages] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    date: "", time: "09:00", pickup: "", tier: null,
    name: "", email: "", phone: "", guests: ""
  });

  useEffect(() => {
    setTour(undefined);
    setForm((prev) => ({ ...prev, tier: null }));
    axios.get(`${BACKEND_URL}/api/tours/${slug}`).then((r) => setTour(r.data)).catch(() => setTour(null));
    axios.get(`${BACKEND_URL}/api/tours`).then((r) => setTourPackages(r.data)).catch(() => {});
  }, [slug]);

  if (tour === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }
  if (!tour) return <Navigate to="/tours" replace />;

  const tier = form.tier || tour.tiers[0];
  const cardFee = Math.round(tier.price * CARD_FEE_PCT) / 100;
  const total = tier.price + cardFee;

  const handleBook = async () => {
    if (!form.date) return toast.error("Pick a date for your tour");
    if (!form.pickup) return toast.error("Enter your pickup address");
    if (!form.name || !form.email || !form.phone) return toast.error("Enter your contact details");
    setLoading(true);
    try {
      const dt = new Date(form.date);
      const [h, m] = form.time.split(":");
      dt.setHours(parseInt(h), parseInt(m), 0, 0);
      const response = await axios.post(`${BACKEND_URL}/api/bookings/guest/checkout`, {
        pickup_location: form.pickup,
        dropoff_location: `${tour.name} (return to pickup)`,
        pickup_datetime: dt.toISOString(),
        passenger_name: form.name,
        passenger_email: form.email,
        passenger_phone: form.phone,
        num_passengers: parseInt(form.guests) || 1,
        vehicle_type: tier.slug,
        booking_type: "hourly",
        hours: tour.hours,
        special_requirements: `TOUR PACKAGE: ${tour.name} (${tour.duration}) — ${tier.label}`,
        price: tier.price,
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        toast.success("Redirecting to secure payment...");
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start booking");
      setLoading(false);
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TouristTrip",
        name: tour.name,
        description: tour.intro,
        touristType: "Groups",
        offers: { "@type": "Offer", price: tour.fromPrice, priceCurrency: "AUD" },
        provider: { "@type": "Organization", name: "Limo Near Me", telephone: "+61481437772", url: "https://www.limonearme.com.au" }
      },
      { "@type": "FAQPage", mainEntity: tour.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }
    ]
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background" data-testid={`tour-page-${tour.slug}`}>
        <Helmet>
          <title>{`${tour.name} | Private Group Tour from $${tour.fromPrice} | Limo Near Me`}</title>
          <meta name="description" content={`${tour.tagline}. ${tour.duration} hosted day out with private bus, driver and itinerary sorted — from $${tour.fromPrice} per group. Book online.`} />
          <link rel="canonical" href={`https://www.limonearme.com.au/tours/${tour.slug}`} />
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
        <PublicHeader />

        {/* Hero */}
        <section className="relative pt-24 min-h-[60vh] flex items-end overflow-hidden">
          <img src={tour.image} alt={tour.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/30" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-amber-400/20 backdrop-blur text-amber-300 text-xs font-semibold flex items-center gap-1"><MapPin className="h-3 w-3" /> {tour.region}</span>
                <span className="px-3 py-1 rounded-full bg-white/10 backdrop-blur text-white text-xs font-semibold flex items-center gap-1"><Clock className="h-3 w-3" /> {tour.duration}</span>
                <span className="px-3 py-1 rounded-full bg-white/10 backdrop-blur text-white text-xs font-semibold flex items-center gap-1"><Users className="h-3 w-3" /> Up to 21 guests</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-3">{tour.name}</h1>
              <p className="text-lg text-gray-200 max-w-2xl">{tour.tagline}</p>
            </motion.div>
          </div>
        </section>

        {/* Content + booking widget */}
        <section className="py-14">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3 space-y-10">
              <p className="text-lg text-muted-foreground leading-relaxed">{tour.intro}</p>

              <div>
                <h2 className="text-xl font-bold mb-5">The day, <span className="font-accent text-amber-700">roughly.</span></h2>
                <div className="space-y-0">
                  {tour.itinerary.map((it, i) => (
                    <div key={i} className="flex gap-4 pb-6 relative">
                      {i < tour.itinerary.length - 1 && <div className="absolute left-[46px] top-8 bottom-0 w-px bg-amber-200" />}
                      <span className="w-24 flex-shrink-0 text-sm font-bold text-amber-700 pt-1">{it.time}</span>
                      <div className="w-3 h-3 rounded-full bg-amber-500 mt-2 flex-shrink-0 -ml-2" />
                      <div>
                        <h3 className="font-semibold">{it.stop}</h3>
                        <p className="text-sm text-muted-foreground">{it.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground italic">Every itinerary is adjusted around your group — this is the popular version.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-amber-50 rounded-2xl p-6">
                  <h3 className="font-bold mb-4">What's included</h3>
                  <ul className="space-y-2">
                    {tour.includes.map((inc, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" /> {inc}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="font-bold mb-4">Good to know</h3>
                  <p className="text-sm text-muted-foreground">{tour.excludes}</p>
                  <p className="text-sm text-muted-foreground mt-3">Bigger than 21 guests? We run multi-vehicle convoys — <a href="https://wa.me/61481437772" target="_blank" rel="noreferrer" className="text-amber-700 font-semibold">message us</a> for a group quote.</p>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-5">Questions, <span className="font-accent text-amber-700">answered.</span></h2>
                <div className="space-y-3">
                  {tour.faqs.map((faq, index) => (
                    <div key={index} className="bg-white rounded-xl border border-black/10 p-5">
                      <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between text-left" data-testid={`tour-faq-${index}`}>
                        <h3 className="font-semibold pr-4 text-sm">{faq.q}</h3>
                        <ChevronDown className={`h-5 w-5 text-amber-600 transition-transform flex-shrink-0 ${openFaq === index ? "rotate-180" : ""}`} />
                      </button>
                      {openFaq === index && <p className="text-muted-foreground text-sm mt-3 pt-3 border-t border-black/10">{faq.a}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Booking widget */}
            <div className="lg:col-span-2">
              <div className="sticky top-32 bg-white rounded-2xl border border-amber-200 shadow-xl p-6" data-testid="tour-book-widget">
                <h3 className="font-bold text-lg mb-1">Book this tour</h3>
                <p className="text-sm text-muted-foreground mb-5">Instant confirmation — pay securely online</p>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Date</label>
                      <div className="relative mt-1">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input type="date" value={form.date} min={new Date().toISOString().split("T")[0]} onChange={(e) => setForm({ ...form, date: e.target.value })} className="pl-9 h-11" data-testid="tour-date-input" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Start time</label>
                      <div className="relative mt-1">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="pl-9 h-11" data-testid="tour-time-input" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Pickup address</label>
                    <div className="mt-1">
                      <AddressAutocomplete
                        value={form.pickup}
                        onChange={(value) => setForm((prev) => ({ ...prev, pickup: value }))}
                        placeholder="Home, hotel or a central meeting spot"
                        icon={MapPin}
                        className="h-11"
                        data-testid="tour-pickup-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Vehicle</label>
                    <div className="grid grid-cols-1 gap-2 mt-1">
                      {tour.tiers.map((t) => (
                        <button
                          key={t.slug}
                          type="button"
                          onClick={() => setForm({ ...form, tier: t })}
                          className={`flex items-center justify-between rounded-xl border p-3 text-left transition-all ${tier.slug === t.slug ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500" : "border-black/10 hover:border-amber-300"}`}
                          data-testid={`tour-tier-${t.slug}`}
                        >
                          <div>
                            <p className="text-sm font-semibold">{t.label}</p>
                            <p className="text-xs text-muted-foreground">{t.seats}</p>
                          </div>
                          <span className="font-bold text-amber-700">${t.price}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="tour-name-input" />
                    <Input placeholder="Guests (e.g. 10)" type="number" min="1" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="h-11" data-testid="tour-guests-input" />
                  </div>
                  <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="tour-email-input" />
                  <Input placeholder="Mobile number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11" data-testid="tour-phone-input" />

                  <div className="border-t border-black/10 pt-4 space-y-1 text-sm">
                    <div className="flex justify-between text-muted-foreground"><span>{tour.name} — {tier.label}</span><span>${tier.price.toFixed(2)}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>Card fee ({CARD_FEE_PCT}%)</span><span>${cardFee.toFixed(2)}</span></div>
                    <div className="flex justify-between font-bold text-base pt-1" data-testid="tour-total"><span>Total (inc. GST)</span><span className="text-amber-700">${total.toFixed(2)}</span></div>
                  </div>

                  <Button className="w-full h-12 rounded-xl font-bold" onClick={handleBook} disabled={loading} data-testid="tour-pay-btn">
                    {loading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CreditCard className="h-5 w-5 mr-2" />}
                    Book & Pay Securely
                  </Button>
                  <a href={`https://wa.me/61481437772?text=${encodeURIComponent(`Hi! I'd like to ask about the ${tour.name} tour.`)}`} target="_blank" rel="noreferrer" className="block">
                    <Button variant="outline" className="w-full h-11 rounded-xl">
                      <MessageCircle className="h-4 w-4 mr-2" /> Ask a Question First
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Other tours */}
        <section className="py-14 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-xl font-bold mb-6">More days <span className="font-accent text-amber-700">out.</span></h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {tourPackages.filter((t) => t.slug !== tour.slug).map((t) => (
                <Link key={t.slug} to={`/tours/${t.slug}`} className="group">
                  <div className="rounded-xl overflow-hidden aspect-[4/3] mb-2">
                    <img src={t.image} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <p className="text-sm font-semibold group-hover:text-amber-700 transition-colors">{t.name}</p>
                  <p className="text-xs text-muted-foreground">From ${t.fromPrice} <ChevronRight className="inline h-3 w-3" /></p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <SeoFooter />
      </div>
    </PageTransition>
  );
}
